<template>
  <div class="video-player">
    <video
      ref="videoRef"
      controls
      playsinline
      :poster="options.poster"
      :muted="$clientJS.isSafari() && autoplay"
      :autoplay="autoplay"
      @ended="$emit('ended', $event)"
    >
      Your device does not support HTML5 video.
      <a :href="options.src">Download video here.</a>
      <source :src="options.src" :type="options.type" />
    </video>
  </div>
</template>

<script lang="ts" setup>
import type { PlayerOptions } from '@/entities/videoPlayer'

const { $clientJS } = useNuxtApp()

// NOTE: July 27, 2023 - Due to a strange issue with Safari, when entering and exiting full screen mode of a video whose parent element has display: flex,
// the video element will be lost/disappear from the DOM. As a result, any parent component wrapping this component should use display: grid

const props = defineProps({
  options: {
    type: Object as PropType<Partial<PlayerOptions>>,
    default: () => ({}),
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['ended', 'mounted', 'play'])

const videoRef = ref<HTMLVideoElement | null>(null)

watch(
  () => props.autoplay,
  autoplay => {
    if (autoplay) {
      emit('play')
      videoRef.value?.play()
    }
  }
)

watch(videoRef, video => {
  if (video) {
    emit('mounted', videoRef.value)
  }
})
</script>

<style lang="stylus" scoped>
.video-player
  @apply w-full h-0 relative
  padding-top 56.25%

  video
    @apply w-full rounded-2xl object-cover h-full absolute top-0 left-0 border border-gray-300

@supports (aspect-ratio: 16 / 9) {
  .video-player {
    @apply w-full h-auto static m-auto
    padding-top 0

    video {
      @apply static m-auto object-contain
      aspect-ratio 16/9
      max-height calc(100vh - 8rem)
      max-width 100%
    }
  }
}
</style>
